import React, { useEffect, useState } from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { VoicesCompanyVoice as CompanyVoice, VoicesConfig } from "../../../Models/voicesConfig";
import { useUpdateVoicesConfig } from "../../../Query/useVoicesConfig";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";

const VOICE_FIELDS: (keyof CompanyVoice)[] = [
  "company_about",
  "company_audience",
  "company_tone",
  "company_never",
];

const MAX_VOICE_CHARS = 2000;

interface Props {
  config: VoicesConfig;
  canManage: boolean;
}

const VoicesCompanyVoice: React.FC<Props> = ({ config, canManage }) => {
  const { t } = useTranslation("voicesSettings");
  const updateConfig = useUpdateVoicesConfig();
  const [form, setForm] = useState<CompanyVoice>({
    company_about: config.company_about,
    company_audience: config.company_audience,
    company_tone: config.company_tone,
    company_never: config.company_never,
  });

  useEffect(
    () =>
      setForm({
        company_about: config.company_about,
        company_audience: config.company_audience,
        company_tone: config.company_tone,
        company_never: config.company_never,
      }),
    [config],
  );

  const changed = VOICE_FIELDS.some((field) => form[field] !== config[field]);
  const valid = VOICE_FIELDS.every((field) => form[field].length <= MAX_VOICE_CHARS);

  return (
    <VoicesCard
      title={t("voiceTitle")}
      subtitle={t("voiceSubtitle")}
      action={
        canManage && (
          <ComoButton
            onClick={() => updateConfig.mutate(form)}
            disabled={!changed || !valid || updateConfig.isPending}
          >
            {t("save")}
          </ComoButton>
        )
      }
    >
      <Stack spacing={2.5}>
        {VOICE_FIELDS.map((field) => (
          <VoicesField key={field} label={t(field)} hint={t(`${field}Hint`)}>
            <VoicesInput
              multiline
              minRows={field === "company_about" ? 3 : 2}
              disabled={!canManage}
              value={form[field]}
              placeholder={t(`${field}Placeholder`)}
              error={form[field].length > MAX_VOICE_CHARS}
              helperText={
                form[field].length > MAX_VOICE_CHARS * 0.8
                  ? t("charactersLeft", { count: MAX_VOICE_CHARS - form[field].length })
                  : undefined
              }
              onChange={(e) => setForm({ ...form, [field]: e.target.value })}
            />
          </VoicesField>
        ))}
      </Stack>
    </VoicesCard>
  );
};

export default VoicesCompanyVoice;
